import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import Title from '../../commonComponents/Title';
import translate from '../../commonComponents/translate';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import ShopModalOrderComplete from './shopItems/ShopModalOrderComplete';
import ShopCheckoutWizard from './steps/ShopCheckoutWizard';
import ShopCartContext from '../../contextProviders/ShopCartContext';
import ShopCheckoutPayment from './steps/ShopCheckoutPayment';
import ShopProductCheckoutShipping from './steps/ShopCheckoutShipping';
import ShopProductCheckoutList from './steps/ShopProductChekoutList';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import CurrencyProp from '../profile/propTypes/currencyProp';
import ShippingProviderProp from './propTypes/shippingProviderProp';
import { hideLoader, showLoader } from '../../services/request';
import Notifications from '../../services/notifications';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import StripeCardProp from '../../commonComponents/propTypes/stripeCardProp';
import {
  compose,
  findCurrencySymbol,
  FrequencySubscriptionTypes,
  handleInitTab,
  PAYMENT_METHOD,
} from '../../services/utilityFunctions';
import withCSRF from '../../commonComponents/withCSRF';
import TenantProp, {
  PAYMENT_PROVIDER,
} from '../../commonComponents/propTypes/tenantProp';
import OrganizationProp from '../../commonComponents/propTypes/organizationProp';
import MarketInfosContext from '../../contextProviders/MarketInfosContext';
import VatTypesProp from '../../commonComponents/forms/Address/VatTypesProp';
import {
  capturePaymentMarketplaceOrder,
  createMarketplaceOrder,
  handleConfirmMarketplacePayment,
} from '../../services/checkoutHelper';
import { useModal } from '../../services/customHooks';
import { BILLING_OPTIONS } from '../../commonComponents/shop/BillingPaymentOptions';
import { handleRulesCompanyBilling } from '../../commonComponents/modal/AcademyPaymentModal';
import MetaContext from '../../contextProviders/MetaContext';
import {
  getCheckoutProductEndpoint,
  getCheckoutProductList,
  getProductsForOrder,
} from './shopHelper';
import BasicModal from '../../commonComponents/modal/BasicModal';
import WarningSection from '../orders/Components/modal/components/WarningSection';

const initialState = {
  checkoutStep: 1,
  priceTotal: 0,
  priceSubTotal: 0,
  showUpdateCart: false,
  products: [],
  coupon: {
    code: '',
    discountValue: 0,
  },
  userTax: 0,
  selectedShippingProvider: null,
  billingAddress: null,
  shippingAddress: null,
  selectedCreditCard: null,
  notes: '',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'init': {
      return initialState;
    }
    case 'goToCart': {
      return { ...state, checkoutStep: 1 };
    }
    case 'goToShipping': {
      return { ...state, checkoutStep: 2 };
    }

    case 'goToPayment': {
      return { ...state, checkoutStep: 3 };
    }
    case 'companyBilling': {
      return { ...state, companyBilling: action.payload };
    }
    case 'updateCartAmount': {
      const updatedProducts = state.products.map(p => {
        if (p.id === action.variationId) {
          return {
            ...p,
            amount: action.amount,
          };
        }
        return p;
      });
      const showUpdateCart =
        updatedProducts.filter(p => {
          const shopItem = action.shopCart.find(s => s.id === p.id);
          return shopItem.amount !== p.amount;
        }).length > 0;
      return {
        ...state,
        products: updatedProducts,
        showUpdateCart,
      };
    }
    case 'updateCoupon': {
      return {
        ...state,
        coupon: {
          code: action.code,
          discountValue: state.coupon.discountValue,
        },
      };
    }
    case 'updateCoins': {
      return {
        ...state,
        isPaidWithCoins: action.isCoinsActive,
      };
    }
    case 'resetCoupon': {
      return {
        ...state,
        coupon: {
          code: '',
          discountValue: 0,
        },
      };
    }
    case 'writeCoupon': {
      return {
        ...state,
        coupon: {
          code: action.code,
          discountValue: state.coupon.discountValue,
        },
      };
    }
    case 'resetProductAmounts': {
      return {
        ...state,
        products: state.products.map(p => {
          const foundCart = action.shopCart.find(s => p.id === s.id);
          return {
            ...p,
            amount: foundCart.amount,
          };
        }),
        showUpdateCart: false,
      };
    }
    case 'updateShippingProvider': {
      return {
        ...state,
        selectedShippingProvider: action.selectedShippingProvider,
      };
    }
    case 'updatePickup': {
      return {
        ...state,
        pickupInfo: action.pickupInfo,
      };
    }
    case 'updateShippingAddress': {
      return {
        ...state,
        shippingAddress: action.payload.shippingAddress,
      };
    }
    case 'updateSelectedAddress': {
      return {
        ...state,
        billingAddress: action.payload.billing,
        shippingAddress: action.payload.shipping,
      };
    }
    case 'updateVat': {
      return {
        ...state,
        priceSubTax: action.vat,
      };
    }
    case 'updateCartProducts': {
      return {
        ...state,
        products: action.products,
      };
    }
    case 'selectCreditCard': {
      return {
        ...state,
        selectedCreditCard: action.cardId,
      };
    }
    case 'setLoading': {
      return {
        ...state,
        isLoading: action.isLoading,
      };
    }
    case 'notes': {
      return { ...state, notes: action.notes };
    }
    case 'updateCartFrequency': {
      return {
        ...state,
        products: state.products?.map(product => {
          if (product.id === action.payload.productId) {
            return {
              ...product,
              frequency: action.payload.newFrequency,
            };
          }
          return product;
        }),
      };
    }
    default:
      console.log('ERROR');
  }
  return null;
};

function ShopCheckoutSteps({
  t,
  _csrf,
  currencies,
  shippingProviders,
  countries,
  creditCards,
  tenant,
  organization,
  vatTypes,
  directSellingActive,
  journeySlug,
}) {
  const { userPortalMenus } = useContext(MetaContext);
  const title =
    userPortalMenus.find(u => u.type === 'marketplace')?.name ||
    t('shop.market_place');
  const { settings = {} } = tenant;
  const { paymentMethods = [], paymentProviderType = PAYMENT_PROVIDER.NONE } =
    settings;
  const [cartState, dispatch] = useReducer(
    reducer,
    initialState,
    () => initialState,
  );
  // Flag to alert if product is digital or not in step 2
  const [isOnlyDigitalProducts, setIsOnlyDigitalProducts] = useState(false);

  const [companyEmails, setCompanyEmails] = useState({
    email: '',
    emails: [],
  });
  const [errors, setErrors] = useState({
    companyBilling: [],
  });
  const [warningModal, setWarningModal] = useModal({
    show: false,
  });

  const { shopCart, setShopCart } = useContext(ShopCartContext);
  const { loggedUser } = useContext(LoggedUserContext);
  const [user, setUser] = useState(loggedUser);
  const isSubscription = useMemo(
    () =>
      cartState?.products.find(
        v => v.frequency !== FrequencySubscriptionTypes.ONLY_ONCE,
      )?.frequency,
    [cartState?.products],
  );

  // console.log("\x1b[35m'Cart Products:'\x1b[0m", cartState.products);

  const { marketInfos } = useContext(MarketInfosContext);
  const [showModal, setShowModal] = useModal({ show: false, data: null });
  const [productsAvailability, setProductsAvailability] = useState([]);
  const [orderDetailPrices, setOrderDetailPrices] = useState({
    total: 0,
    subtotal: 0,
    discount: 0,
    userDiscount: 0,
    userDiscountPercentage: 0,
    tax: 0,
    isPaidWithCoins: false,
  });
  const [billingActive, setBillingActive] = useState(
    BILLING_OPTIONS.YOUR_BILLING,
  );
  const [paymentId, setPaymentId] = useState(null);
  const [tabActive, setTabActive] = useState(null);

  useEffect(() => {
    if (!tabActive && cartState?.checkoutStep === 3) {
      setTabActive(handleInitTab(paymentMethods));
    }
  }, [cartState?.checkoutStep]);
  const handleBillingChange = billingOption => {
    setBillingActive(billingOption);
    const newPaymentTab =
      billingOption === BILLING_OPTIONS.YOUR_BILLING
        ? paymentMethods[0]
        : PAYMENT_METHOD.PAID_BY_COMPANY;
    if (billingOption === BILLING_OPTIONS.COMPANY_BILLING) {
      const initialShippingAddressState =
        !loggedUser?.businessType || loggedUser?.businessType === 'individual'
          ? user.shippingAddress.filter(
              address => address.countryCode === user.shippingCountry,
            )[0]
          : user.shippingAddress[0];
      dispatch({
        type: 'updateShippingAddress',
        payload: {
          shippingAddress: initialShippingAddressState || null,
        },
      });
    }
    setTabActive(newPaymentTab);
  };
  const [isPaidWithCoins, setIsPaidWithCoins] = useState(false);
  // region BEGIN: Credit Cards
  const [userCreditCards, setUserCreditCards] = useState([]);

  // Function on client side once to make sure credit cards are updated when user changes address
  const getCreditCards = (isFirstRender = false) => {
    // Only call /credit-card/list if user is logged
    if (loggedUser?._id) {
      axios
        .get('/api/credit-card/list?isContextAcademy=false', {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'License',
          },
        })
        .then(({ data: response }) => {
          // if card doesn't belong to this stripe account reset state
          if (
            cartState.selectedCreditCard &&
            !response.data.find(c => c.id === cartState.selectedCreditCard)
          ) {
            dispatch({
              type: 'selectCreditCard',
              cardId: null,
            });
          }
          if (isFirstRender) {
            dispatch({
              type: 'selectCreditCard',
              cardId:
                response.data.find(c => c.isDefault)?.id ||
                (response.data.length > 0 ? response.data[0].id : null),
            });
          }
          setUserCreditCards(response.data);
        })
        .catch(err => {
          console.error(err);
          Notifications.showErrorFromRequest(err, 'ERROR_UNKNOWN');
        });
    }
  };
  useEffect(() => {
    getCreditCards(true);
  }, []);
  useEffect(() => {
    getCreditCards();
  }, [cartState?.billingAddress, cartState?.shippingAddress]);
  // endregion END: Credit Cards

  /** region Handle Subscription Change */
  // CHANGE
  const handleWarningModalClose = useCallback(
    (save, data) => {
      // setWarningModal({ show: false, data: null });
      if (data) {
        if (save) {
          const updatedProducts = cartState?.products
            ?.map(product => {
              if (product.id === data.productId) {
                return { ...product, frequency: data.newFrequency };
              }
              return null;
            })
            .filter(p => p);
          setShopCart(
            updatedProducts.map(product => ({
              id: product.id,
              price: product.priceWithoutVAT / 100,
              minorPrice: product.priceWithVAT / 100,
              amount: product.amount,
              va: product.va,
              cv: product.cv,
              commissionable: product.commissionable,
              image: product?.images[0],
              isPack: product.isPack,
              subscriptionType: product.frequency,
              name: product.name,
            })),
          ); // update cookies
        } else {
          $(`#availableFrequencies_${data.productId}`)
            .val(data.prevFrequency)
            .trigger('change');
          setWarningModal({ show: false, data: null });
        }
      }
    },
    [setWarningModal, cartState?.products],
  );
  // endregion Warning

  const handleChangeProductFrequency = useCallback(
    (productId, prevFrequency) => newFrequency => {
      // if selecting subscription, and there is more products in card
      if (
        newFrequency !== FrequencySubscriptionTypes.ONLY_ONCE &&
        cartState?.products?.length > 1
      ) {
        setWarningModal({
          show: true,
          data: { productId, newFrequency, prevFrequency },
        });
      } else {
        dispatch({
          type: 'updateCartFrequency',
          payload: {
            productId,
            newFrequency,
            prevFrequency,
          },
        });
      }
    },
    [cartState?.products],
  );

  /** endregion Handle Subscription Change */

  const userCurrencyCode =
    loggedUser && loggedUser.currency
      ? loggedUser.currency.toUpperCase()
      : marketInfos.localCurrency;
  const userCurrency = currencies.find(c => c.code === userCurrencyCode) || {};

  const handleCompanyEmails = (type, value) => {
    setCompanyEmails(prevState => ({
      ...prevState,
      [type]: value,
    }));
  };

  // Scroll into view in mobile
  useEffect(() => {
    if (window.innerWidth < 455) {
      const id = 'checkoutTabsId';
      const yOffset = 400;
      const element = document.getElementById(id);
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [cartState?.checkoutStep]);
  const updateCartAmount = (variationId, amount) => {
    dispatch({ type: 'updateCartAmount', variationId, amount, shopCart });
  };

  const updateCouponString = data => {
    dispatch({ type: 'writeCoupon', code: data });
  };

  const updatedAmounts = () => {
    setShopCart(
      shopCart.map(s => {
        const foundProduct = cartState.products.find(p => p.id === s.id);
        return {
          ...s,
          amount: foundProduct.amount,
        };
      }),
    );
  };

  const resetAmounts = () => {
    dispatch({ type: 'resetProductAmounts', shopCart });
  };

  const deleteProduct = variationId => {
    setShopCart(shopCart.filter(s => s.id !== variationId));
  };

  const goBack = (step, cartAvailability) => {
    if (step === 'shipping') {
      if (cartAvailability && cartAvailability.length > 0) {
        setProductsAvailability(cartAvailability);
        Notifications.showNotificationError(
          t('error'),
          t('shop.checkout.availability.products'),
        );
      }
      dispatch({ type: 'goToCart' });
    }
    if (step === 'payment') {
      dispatch({ type: 'goToShipping' });
    }
  };

  const goToShipping = () => {
    const frequencies = cartState?.products?.map(item => item.frequency);
    const parentProducts = cartState?.products?.map(item => item.productId);

    // Create a Set from the frequencies to eliminate duplicates
    const uniqueFrequencies = new Set(frequencies);
    const uniqueParentProducts = new Set(parentProducts);

    // Check if there are different frequencies
    const hasDifferentFrequencies = uniqueFrequencies.size > 1;
    const hasDifferentParentProducts = uniqueParentProducts.size > 1;

    // Count the number of frequencies that are not ONLY_ONCE
    const nonOnlyOnceFrequenciesCount = frequencies?.reduce(
      (count, f) =>
        f !== FrequencySubscriptionTypes.ONLY_ONCE ? count + 1 : count,
      0,
    );

    // Prevent that user cannot continue with different frequencies and different parent products
    const isAbleToContinue =
      // Case 1: If there's only one parent product and all frequencies are the same
      (!hasDifferentParentProducts && !hasDifferentFrequencies) ||
      // Case 2: If there are different parent products and more than one frequency is not ONLY_ONCE
      (hasDifferentParentProducts && nonOnlyOnceFrequenciesCount === 0);

    if (hasDifferentFrequencies || !isAbleToContinue) {
      Notifications.showNotificationError(
        t('error'),
        t('warning.product.subscription'),
      );
      return;
    }
    dispatch({ type: 'goToShipping' });
  };

  /**
   * Handles the errors of Tab 2 "Invoice to company"
   * @returns {boolean} // Should return true or false, to stop also the calling function
   */
  const handleCompanyErrors = () => {
    if (billingActive === BILLING_OPTIONS.COMPANY_BILLING) {
      if (
        handleRulesCompanyBilling(
          { ...cartState?.companyBilling, ...companyEmails },
          true,
        )?.emailErrors?.length > 0 ||
        handleRulesCompanyBilling(
          { ...cartState?.companyBilling, ...companyEmails },
          true,
        )?.errors?.length > 0
      ) {
        setErrors(prevState => ({
          ...prevState,
          companyBilling: handleRulesCompanyBilling(
            { ...cartState?.companyBilling, ...companyEmails },
            true,
          ),
        }));
        return true;
      }
      setErrors(prevState => ({ ...prevState, companyBilling: [] }));
      setTabActive(PAYMENT_METHOD.PAID_BY_COMPANY);
      if (
        !cartState?.shippingAddress?.address_1 ||
        !cartState?.shippingAddress?.countryCode
      ) {
        Notifications.showNotificationError(
          t('error'),
          t('shop.shippingAddress.required'),
        );
        return true;
      }
      if (
        cartState?.companyBilling?.taxCode &&
        cartState?.companyBilling?.address?.countryCode
      ) {
        if (
          vatTypes.find(v => v.taxCode === cartState?.companyBilling?.taxCode)
            ?.countryCode !==
          countries.find(
            a => a.code === cartState?.companyBilling?.address?.countryCode,
          )?.code
        ) {
          Notifications.showNotificationError(
            t('error'),
            t('shop.error.vatType.country'),
          );
          return true;
        }
      }

      if (!isOnlyDigitalProducts && !cartState?.selectedShippingProvider) {
        Notifications.showNotificationError(
          t('error'),
          t('order_create_fail_no_shippingProvider'),
        );
        return true;
      }
    }
    return false;
  };
  const goToPayment = () => {
    if (billingActive === BILLING_OPTIONS.YOUR_BILLING) {
      if (!cartState?.billingAddress || !cartState?.billingAddress.address_1) {
        setErrors(prevState => ({ ...prevState, billingAddress: true }));
        return;
      }
    }

    dispatch({ type: 'goToPayment' });
  };

  const updateSelectedAddress = checkedAddress => {
    dispatch({ type: 'updateSelectedAddress', payload: checkedAddress });
  };

  const handleCompanyBilling = companyBilling => {
    dispatch({ type: 'companyBilling', payload: companyBilling });
  };

  const updateCartCoupon = textCoupon => {
    dispatch({ type: 'updateCoupon', code: textCoupon });
  };

  const updateCoins = isCoinsActive => {
    setIsPaidWithCoins(isCoinsActive);
  };

  const selectCreditCard = cardId => {
    dispatch({ type: 'selectCreditCard', cardId });
  };

  const onChangeNotes = notes => {
    dispatch({ type: 'notes', notes });
  };

  const loadingPurchase = isLoading => {
    if (isLoading) {
      showLoader();
    } else {
      hideLoader();
    }
    dispatch({ type: 'setLoading', isLoading });
  };

  const [orderInfoCreated, setInfoCreated] = useState(null);
  /** BANCONTACT */
  const stripe = useStripe();
  const elements = useElements();

  const returnAddress = address => {
    const address_2 = address?.address_2 ? `, ${address?.address_2}` : '';
    let value = `${address?.address_1}${address_2}, ${address?.zipCode} ${address?.city}, ${address?.country}`;
    if (!address?.address_1) {
      value = '';
    }
    return value;
  };

  // REMOVE SINGLE USE CARDS
  const onUnloadPage = isConfirmPayment => {
    if (isConfirmPayment) {
      setShopCart([]);
    }
  };

  const createOrder = async (paymentMethod, accountHolderName) => {
    try {
      // If Account Holder Name length < 3 or does not exist show notification error
      if (paymentMethod === 'bancontact') {
        if (!(accountHolderName && accountHolderName.length >= 3)) {
          Notifications.showNotificationError(
            t('error'),
            t('ERROR_BANCONTACT_INVALID'),
          );
          return;
        }
      }
      loadingPurchase(true);

      let paymentMethodToSend = paymentMethod;

      /** region HANDLE INFOS FOR ORDER */
      const orderData = {
        billingAddress: cartState.billingAddress,
        shippingAddress: cartState.shippingAddress || cartState.billingAddress,
        shipping: cartState?.selectedShippingProvider?._id || null,
        discount: cartState?.coupon?.code || null,
        paymentMethodId:
          paymentMethod === PAYMENT_METHOD.CREDIT_CARD
            ? cartState.selectedCreditCard
            : null,
        notes: cartState.notes,
        products: getProductsForOrder(cartState.products),
        paymentMethodType: paymentMethodToSend,
        orderInfoCreated,
        country: orderDetailPrices.country,
        currency: orderDetailPrices.currency,
        isPaidWithCoins: orderDetailPrices.isPaidWithCoins,
      };

      if (paymentMethodToSend === PAYMENT_METHOD.PAID_BY_COMPANY) {
        orderData.companyBilling = {
          ...cartState.companyBilling,
          ...companyEmails,
        };
        delete orderData.billingAddress;
        delete orderData?.paymentId;
      } else {
        delete orderData.companyBilling;
      }

      // Check if card is single use
      const isSingleUsageCard =
        userCreditCards?.length > 0 &&
        cartState.selectedCreditCard &&
        userCreditCards.find(c => c.id === cartState.selectedCreditCard)
          ?.usage === 'single_use';

      if (
        paymentMethodToSend === PAYMENT_METHOD.CREDIT_CARD &&
        isSingleUsageCard
      ) {
        orderData.paymentMethodType = PAYMENT_METHOD.CREDIT_CARD_SOURCE;
        paymentMethodToSend = PAYMENT_METHOD.CREDIT_CARD_SOURCE;
      }

      if (
        cartState?.selectedShippingProvider?.providerType === 'mondial_relay'
      ) {
        orderData.shippingPickupId = cartState?.pickupInfo?.pickupId;
        delete cartState?.pickupInfo?.pickupId;
        const shippingPickupAddress = {
          ...cartState?.pickupInfo,
          country: countries.find(
            c => c.code === cartState?.pickupInfo.countryCode,
          ).name,
        };
        const addressLine = returnAddress(shippingPickupAddress);
        orderData.shippingPickupAddress = {
          ...shippingPickupAddress,
          address: addressLine,
        };
        orderData.shippingPickupType = 'mondial_relay';
      }

      // In the case of order total is 0 we deleted the paymentId, not needed
      if (orderDetailPrices.total === 0) {
        delete orderData.paymentId;
      }
      if (isSubscription) {
        orderData.frequency = isSubscription;
      }
      /** endregion HANDLE INFOS FOR ORDER */

      /** POST: CREATE MARKETPLACE ORDER */
      let orderResponse;
      let orderPaymentId;
      let orderPaymentMethod;
      if (!orderInfoCreated) {
        const {
          orderData: orderDataResponse,
          paymentMethodId: orderDataPaymentId,
          paymentMethod: orderDataPaymentMethod,
        } = await createMarketplaceOrder(
          _csrf,
          orderData,
          cartState.selectedCreditCard,
          paymentMethodToSend,
        );

        orderResponse = orderDataResponse;
        orderPaymentId = orderDataPaymentId;
        orderPaymentMethod = orderDataPaymentMethod;

        // Added orderResponse to state, so if the payment fails we have access to it
        setInfoCreated(orderDataResponse);
      } else {
        orderResponse = orderInfoCreated;
        orderPaymentId = cartState.selectedCreditCard;
        orderPaymentMethod = paymentMethodToSend;
      }

      if (orderResponse._id) {
        /** POST/PUT: CAPTURE PAYMENT / UPDATE PAYMENT INTENT MARKETPLACE ORDER */
        const result = await capturePaymentMarketplaceOrder(
          t,
          _csrf,
          orderResponse,
          orderPaymentId,
          orderPaymentMethod,
          orderInfoCreated,
          paymentId,
          paymentProviderType,
          false,
          stripe,
          elements,
        );
        if (!result) {
          return;
        }
        const {
          client_secret,
          status,
          id: paymentIntentId,
          payPalPaymentId, // manages paypal confirmation for subscription
        } = result;
        setPaymentId(paymentIntentId);

        /** POST: CONFIRM PAYMENT MARKETPLACE ORDER */
        await handleConfirmMarketplacePayment(
          _csrf,
          orderResponse._id,
          status,
          orderPaymentMethod,
          paymentProviderType,
          paymentIntentId,
          cartState.selectedCreditCard,
          client_secret,
          stripe,
          elements,
          accountHolderName,
          () => onUnloadPage(true),
          orderResponse,
          payPalPaymentId,
        );
      }

      // Open Success modal
      setShowModal({
        show: true,
        data: {
          urlGoBack: journeySlug ? `/journey/${journeySlug}` : null,
          goBackMessage: journeySlug
            ? t('shop.order_modal_back_journey')
            : null,
        },
      });
      // Ended the loading state
      loadingPurchase(false);
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'ERROR_PAYMENT_DENIED');
      loadingPurchase(false);
    }
  };

  useEffect(() => {
    if (creditCards && creditCards.length !== 0) {
      const defaultCard = creditCards.filter(c => c.isDefault === true);
      if (defaultCard[0] && defaultCard[0].id) {
        selectCreditCard(defaultCard[0].id);
      }
    }
    onUnloadPage();
  }, []);

  const handleClearPaidWithCoins = paidWithCoins =>
    setIsPaidWithCoins(paidWithCoins);
  const [isShippingDifferent, setIsShippingDifferent] = useState(false);
  const checkIfShippingIsDifferent = shippingDifferent => {
    setIsShippingDifferent(shippingDifferent);
  };
  const renderStep = () => {
    const userLogged = user || {};
    const { address, firstName, lastName, email, phone, personalInfo } =
      userLogged;
    if (address) {
      if (firstName) {
        address.firstName = firstName;
      }
      if (lastName) {
        address.lastName = lastName;
      }
      if (email) {
        address.email = email;
      }
      if (phone) {
        address.phone = phone;
      }
      const isIndividual =
        personalInfo?.businessType === 'individual' ||
        !personalInfo?.businessType;
      if (!isIndividual && personalInfo?.companyName) {
        address.companyName = personalInfo?.companyName || '';
      }
    }
    let companyBilling = cartState?.companyBilling;
    if (cartState?.companyBilling && companyEmails) {
      companyBilling = {
        ...cartState.companyBilling,
        emails: [companyEmails?.email, ...companyEmails?.emails],
      };
    }
    switch (cartState.checkoutStep) {
      case 2:
        return (
          <ShopProductCheckoutShipping
            goToPayment={goToPayment}
            isShippingDifferent={isShippingDifferent}
            goBack={goBack}
            user={user}
            updateUser={data => setUser(data)}
            shippingProviders={shippingProviders}
            cartState={cartState}
            countries={countries}
            updateSelectedAddress={updateSelectedAddress}
            updateSelectedShippingProvider={selectedShippingProvider =>
              dispatch({
                type: 'updateShippingProvider',
                selectedShippingProvider,
              })
            }
            updatePickup={pickupInfo =>
              dispatch({ type: 'updatePickup', pickupInfo })
            }
            organization={organization}
            orderDetails={orderDetailPrices}
            checkIfShippingIsDifferent={checkIfShippingIsDifferent}
            vatTypes={vatTypes}
            isOnlyDigitalProducts={isOnlyDigitalProducts}
            orderAlreadyCreated={orderInfoCreated}
            tenant={tenant}
            handleBillingChange={handleBillingChange}
            billingActive={billingActive}
            onCompanyBillingChange={handleCompanyBilling}
            companyBilling={cartState?.companyBilling}
            errors={errors}
            companyEmails={companyEmails}
            onCompanyEmailsChange={handleCompanyEmails}
            onCompanyErrors={handleCompanyErrors}
            isSubscription={isSubscription}
          />
        );
      case 3:
        return (
          <ShopCheckoutPayment
            t={t}
            tenant={tenant}
            organization={organization}
            goBack={goBack}
            userCurrency={userCurrency}
            cartState={cartState}
            creditCards={userCreditCards}
            updateCreditCards={cardsList => setUserCreditCards(cardsList)}
            selectCreditCard={selectCreditCard}
            createOrder={createOrder}
            onChangeNotes={onChangeNotes}
            orderDetails={orderDetailPrices}
            isPaidWithCoins={isPaidWithCoins}
            clearPaidWithCoins={handleClearPaidWithCoins}
            orderAlreadyCreated={orderInfoCreated}
            vatTypes={vatTypes}
            countries={countries}
            handleCompanyBilling={handleCompanyBilling}
            directSellingActive={directSellingActive}
            tabActive={tabActive}
            companyBilling={companyBilling}
            billingActive={billingActive}
            onHandlePaymentTabs={tab => setTabActive(tab)}
            isSubscription={isSubscription}
          />
        );
      default:
        return (
          <ShopProductCheckoutList
            cartState={cartState}
            userCurrency={userCurrency}
            goToShipping={goToShipping}
            updateCouponString={updateCouponString}
            updatedAmounts={updatedAmounts}
            resetAmounts={resetAmounts}
            updateCartAmount={updateCartAmount}
            updateCartCoupon={updateCartCoupon}
            deleteProduct={deleteProduct}
            _csrf={_csrf}
            orderDetails={orderDetailPrices}
            productsAvailability={productsAvailability}
            isPaidWithCoins={isPaidWithCoins}
            clearPaidWithCoins={handleClearPaidWithCoins}
            orderAlreadyCreated={orderInfoCreated}
            directSellingActive={directSellingActive}
            isSubscription={isSubscription}
            onUpdateProductFrequency={handleChangeProductFrequency}
            updateCoins={updateCoins}
          />
        );
    }
  };

  const closeModal = () => {
    setShopCart([]);
    window.location.href = '/shop';
  };

  const goToOrder = () => {
    setShopCart([]);
    window.location.href = '/shop/order';
  };

  /** CHECK WITH THE API THE CORRECT VALUES TO DISPLAY IN ORDER */
  const checkOrderDetail = newProducts => {
    const address = loggedUser && loggedUser.address ? loggedUser.address : {};
    const currency =
      loggedUser && loggedUser.currency
        ? loggedUser.currency
        : marketInfos.localCurrency;
    const personalInfo =
      loggedUser && loggedUser.currency ? loggedUser.personalInfo : {};
    const shippingCountry =
      loggedUser && loggedUser.shippingCountry
        ? loggedUser.shippingCountry
        : marketInfos.localCountry;
    const { businessType } = personalInfo || {};

    const productsCart =
      cartState?.products.length > 0 ? cartState.products : newProducts;
    // Filter by the digital ones
    const hasDigitalInCart = productsCart.filter(
      p => p.isDigitalContent === true,
    );
    // Check if all is digital by comparing the products cart length with the actually digital products in cart
    setIsOnlyDigitalProducts(hasDigitalInCart.length === productsCart.length);

    const params = {
      products: getProductsForOrder(productsCart),
      isPaidWithCoins,
    };
    if (cartState.coupon && cartState.coupon.code) {
      params.discount = cartState.coupon.code;
    }
    if (cartState.billingAddress) {
      params.billingAddress = cartState.billingAddress;
    }
    if (cartState.shippingAddress) {
      params.shippingAddress = cartState.shippingAddress;
    }
    if (
      billingActive === BILLING_OPTIONS.YOUR_BILLING &&
      ((!isShippingDifferent && cartState.billingAddress) ||
        (isShippingDifferent && !cartState.shippingAddress))
    ) {
      params.shippingAddress = cartState.billingAddress;
    }
    if (
      billingActive === BILLING_OPTIONS.YOUR_BILLING &&
      cartState.selectedShippingProvider
    ) {
      params.shipping = cartState.selectedShippingProvider._id;
    }

    params.currency = currency || 'eur';

    if (!businessType || businessType === 'individual') {
      params.country = shippingCountry || address.countryCode;
    } else {
      params.country =
        cartState?.billingAddress?.countryCode ||
        address?.countryCode ||
        shippingCountry;
    }

    if (
      billingActive === BILLING_OPTIONS.COMPANY_BILLING &&
      cartState?.companyBilling?.address?.countryCode &&
      cartState.shippingAddress
    ) {
      params.companyBilling = {
        ...cartState.companyBilling,
        ...companyEmails,
      };
      params.paymentMethodType = PAYMENT_METHOD.PAID_BY_COMPANY;
      if (cartState?.selectedShippingProvider) {
        params.shipping = cartState.selectedShippingProvider._id;
      }
      delete params.billingAddress;
    } else {
      delete params.companyBilling;
    }
    if (isSubscription) {
      params.frequency = isSubscription;
    }

    if (tabActive) params.paymentMethodType = tabActive;

    const url = `/api/market/order/calculate?populate=products.product&country=${params.country}&currency=${params.currency}`;
    axios
      .post(url, params, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        // Destructing response
        const { data } = response;
        const {
          pricesWithoutVAT,
          discountTotal,
          subtotal,
          userDiscountTotal,
          userDiscount,
          total,
          subtotalTax,
          shippingTotal,
          products,
          shippingTax,
          shippingSubTotal,
          currency: currencyData,
          isPaidWithCoins: isPaidWithCoinsData,
          totalVA,
          totalCV,
          fee,
          subtotalTaxAllIncluded,
          subtotalExtraTax,
        } = data;
        const symbol = findCurrencySymbol(currencyData, currencies);
        const productsToUpdate = productsCart.map(cartProduct => {
          const product = products.find(
            productData =>
              (cartProduct?.isPack
                ? productData.packProduct
                : productData.product) === cartProduct.id,
          );

          if (product) {
            return {
              ...cartProduct,
              priceWithoutVAT: product.subtotal,
              priceWithVAT: product.total,
              tax: product.tax,
              symbol,
              packVariations:
                product?.packVariations?.map(v => ({
                  variation: v?.variation?._id,
                  quantity: v.quantity,
                  stockQuantity: v?.variation?.stockQuantity,
                })) || [],
            };
          }
          return cartProduct;
        });
        dispatch({
          type: 'updateCartProducts',
          products: productsToUpdate,
        });

        setOrderDetailPrices({
          subtotal,
          userDiscountPercentage: userDiscount,
          userDiscount: userDiscountTotal,
          total,
          tax: subtotalTax,
          shippingSubTotalTax: data.shippingSubTotalTax || 0,
          subtotalTaxAllIncluded,
          pricesWithoutVAT,
          discount: discountTotal,
          coinsDiscountTotal: data.coinsDiscountTotal || 0,
          shippingDiscountTotal: data.shippingDiscountTotal || 0,
          shippingTotal,
          shippingTax,
          shippingSubTotal,
          currency: currencyData,
          symbol,
          country: params.country,
          isPaidWithCoins: isPaidWithCoinsData,
          totalVA,
          totalCV,
          fee: fee || {},
          subtotalExtraTax,
        });
      })
      .catch(err => {
        if (err?.response?.data?.error === 'ERROR_COUPON_DISCOUNT_NOT_FOUND') {
          updateCartCoupon('');
        }
        Notifications.showErrorFromRequest(err, 'error_fetchProductsList');
        console.log(err);
      });
  };

  useEffect(() => {
    // we need to make sure that it's only checking if there are products in the state
    if (cartState.products.length > 0) {
      checkOrderDetail();
    }
  }, [
    cartState?.coupon,
    cartState.billingAddress?.countryCode,
    cartState.shippingAddress?.countryCode,
    cartState.selectedShippingProvider,
    isPaidWithCoins,
    cartState?.companyBilling?.countryCode,
    tabActive,
  ]);

  useEffect(() => {
    if (cartState.products && cartState.products.length === 0) {
      const { endpoint, products, variations } =
        getCheckoutProductEndpoint(shopCart);
      if (
        (variations?.length > 0 || products?.length > 0) &&
        !cartState.products.length
      ) {
        axios
          .get(endpoint, {
            params: {
              populate: [
                'variations',
                'category',
                'owner',
                'variations.stock.warehouse',
              ],
            },
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
            },
          })
          .then(({ data: response }) => {
            const checkoutProducts = getCheckoutProductList(
              t,
              false,
              variations,
              products,
              shopCart,
              response.data,
            );
            checkOrderDetail(checkoutProducts);
          })
          .catch(err => {
            Notifications.showErrorFromRequest(err, 'error_fetchProductsList');
            console.log(err);
          });
      }

      window.mLayout.initHeader();
    }
  }, [shopCart]);

  return (
    <>
      {warningModal.show ? (
        <BasicModal
          onClose={handleWarningModalClose}
          id="notAllowedModal"
          saveMessage={t('yes')}
          data={warningModal.data}
        >
          <WarningSection />
        </BasicModal>
      ) : null}
      {showModal.show && (
        <ShopModalOrderComplete
          closeModal={closeModal}
          confirmModal={goToOrder}
          urlGoBack={showModal.data.urlGoBack}
          goBackMessage={showModal.data.goBackMessage}
        />
      )}
      <Title title={title}>
        <ContentContainer>
          <SubHeader title={title} />
          <div className="m-content shop">
            <div className="row" id="checkoutTabsId">
              <div className="col-lg-12">
                <ShopCheckoutWizard ActiveStep={cartState.checkoutStep} />
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12">{renderStep()}</div>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

ShopCheckoutSteps.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  shippingProviders: PropTypes.arrayOf(
    PropTypes.shape(ShippingProviderProp.propType),
  ).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  creditCards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType))
    .isRequired,
  tenant: PropTypes.shape(TenantProp.propTypes),
  organization: PropTypes.shape(OrganizationProp.propTypes),
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  directSellingActive: PropTypes.bool.isRequired,
  journeySlug: PropTypes.string,
};

ShopCheckoutSteps.defaultProps = {
  tenant: {},
  organization: {},
  journeySlug: null,
};

export default compose(translate, withCSRF)(ShopCheckoutSteps);
